"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type ConsentLevel = 'all' | 'essential' | null;

interface CookieContextType {
  consent: ConsentLevel;
  setConsent: (consent: ConsentLevel) => void;
  hasConsented: boolean;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export function CookieProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentLevel>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedConsent = localStorage.getItem('cookieConsent') as ConsentLevel;
    if (savedConsent) {
      setConsent(savedConsent);
    }
  }, []);

  const handleSetConsent = (newConsent: ConsentLevel) => {
    setConsent(newConsent);
    if (newConsent) {
      localStorage.setItem('cookieConsent', newConsent);
    }
  };

  // Avoid hydration mismatch by not rendering until mounted
  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <CookieContext.Provider
      value={{
        consent,
        setConsent: handleSetConsent,
        hasConsented: consent !== null,
      }}
    >
      {children}
    </CookieContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieContext);
  if (context === undefined) {
    // Return default values during SSR or if provider is not available
    return {
      consent: null,
      setConsent: () => {},
      hasConsented: false,
    };
  }
  return context;
}
